import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardBodyClass,
  btnPrimaryClass,
  btnSecondaryClass,
  btnDangerClass,
  formLabelClass,
  formControlClass,
  alertErrorClass,
  badgeClass,
  modalOverlayClass,
  modalPanelClass,
  modalHeaderClass,
  modalBodyClass,
  modalFooterClass,
  modalCloseClass,
  cardTitleClass,
  spinnerClass,
} from "../dashboardStyles";
import {
  GetCalendarBookingsAction,
  GetResourcesAction,
  GetServicesAction,
  UpdateBookingAction,
  UpdateBookingStatusAction,
  DeleteBookingAction,
  getUserInfoFromToken,
} from "../../../store/action";
import { UserRole } from "../../../constants/roles";
import { formatSlotLabel, today } from "../../../utils/dates";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import QuickBookingModal from "./QuickBookingModal";
import { notify, notifySaveError } from "../../../utils/notify";
import { BookingStatusNoticeKeys } from "../../../constants/enums";
import "./calendar.css";

/**
 * Booking calendar.
 *
 * Times are rendered in UTC on purpose. BookingDateTime carries wall-clock
 * numbers stamped as UTC, and FullCalendar is configured with timeZone="UTC".
 * Pass it the stamped instant directly: rebuilding it as a local Date first
 * shifts the grid by the viewer's offset (09:00 becomes 06:00 in Bucharest).
 */

const STATUS_COLORS = {
  pending: { bg: "#fef3c7", border: "#f59e0b", text: "#92400e" },
  confirmed: { bg: "#dcfce7", border: "#16a34a", text: "#166534" },
  completed: { bg: "#e0f2fe", border: "#0284c7", text: "#075985" },
  cancelled: { bg: "#fee2e2", border: "#dc2626", text: "#991b1b" },
};

const statusKey = (status) => (status || "").toLowerCase();

/**
 * What a drag writes back to the API.
 *
 * FullCalendar's UTC grid returns a Date whose UTC fields are the wall-clock
 * numbers shown on screen. Read those same fields when saving a drag.
 *
 * The slot form is the canonical wire contract from `AvailabilityServices`:
 * 24-hour, with the decorative AM/PM suffix.
 */
export const toApiTimeSlot = (date) => {
  const hour = date.getUTCHours();
  const minute = date.getUTCMinutes();
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
};

export const toApiDate = (date) => date.toISOString().slice(0, 10);

/** How far apart the grid's lines are drawn. */
const SLOT_MINUTES = 30;

/**
 * Where a drag or a click lands: the quarter hour, the same step
 * `AvailabilityServices` offers, so a booking can be moved into the gap a
 * 45-minute one leaves. A selection exactly this long is a click, not a drag.
 */
const SNAP_MINUTES = 15;

/**
 * What a selection on the grid prefills in the quick-booking form.
 *
 * Read off the UTC fields for the same reason as a drag (see above). A month
 * cell carries a day and no time. A single grid cell is a click and says
 * nothing about length; a longer stretch is a drag, and its length picks the
 * service that fits it.
 */
export const selectionToQuickBooking = ({ start, end, allDay }) => {
  const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
  return {
    date: toApiDate(start),
    time: allDay ? "" : formatSlotLabel(toApiTimeSlot(start)),
    durationMinutes: !allDay && minutes > SNAP_MINUTES ? minutes : null,
  };
};

/**
 * "Now" in the grid's fiction: the viewer's wall clock written into UTC fields,
 * which is how every booking on the grid is placed. Good enough to stop a
 * selection in the past; the API makes the real check in the business's zone.
 */
const nowOnGrid = () => {
  const now = new Date();
  return Date.UTC(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    now.getHours(),
    now.getMinutes(),
  );
};

/** The view each layout opens on; see the toolbar below for why they differ. */
const DESKTOP_VIEW = "timeGridWeek";
const MOBILE_VIEW = "listDay";

/**
 * A booking as a row of the list views: the customer, then what and with whom.
 * The grid has no room for the second line; an agenda read on a phone is the
 * place it is most wanted, since the row is all there is.
 */
const renderListEvent = ({ event }) => {
  const { serviceName, resourceName } = event.extendedProps.booking;
  const detail = [serviceName, resourceName].filter(Boolean).join(" · ");
  return (
    <span className="block min-w-0">
      <span className="block font-semibold break-words text-ink-900">{event.title}</span>
      {detail && <span className="block text-xs break-words text-ink-500">{detail}</span>}
    </span>
  );
};

const Calendar = () => {
  const { t, i18n } = useTranslation();
  const calendarRef = useRef(null);

  const [bookings, setBookings] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  // Services feed only the quick-booking form, so they load in the background
  // and their absence never blocks the calendar.
  const [services, setServices] = useState([]);

  // What the quick-booking form opens with ({ date, time, durationMinutes }),
  // or null while it is closed.
  const [quickBooking, setQuickBooking] = useState(null);

  // The window FullCalendar is currently showing. Fetches are driven by this
  // rather than by "today" so paging back a month loads that month.
  const [range, setRange] = useState(null);

  const [resourceFilter, setResourceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Narrow the calendar to the caller's own resources. Employees are confined
  // to theirs by the API whatever this says, so the toggle is only offered to
  // admins — who are often also the person doing the work in a small company.
  const [onlyMine, setOnlyMine] = useState(false);

  const isMobile = useMediaQuery("(max-width: 640px)");

  useEffect(() => {
    setUserInfo(getUserInfoFromToken());
  }, []);

  const isAdmin =
    userInfo &&
    (userInfo.role === UserRole.SYSTEM_OWNER ||
      userInfo.role === UserRole.ADMIN ||
      userInfo.role === UserRole.COMPANY_ADMIN);

  // Filtering to "only mine" with no resource assigned yields an empty
  // calendar, which reads as a bug rather than a setting. Detect it so the UI
  // can say why.
  const ownsAnyResource =
    !!userInfo?.userId && resources.some((r) => r.userId === userInfo.userId);

  useEffect(() => {
    GetResourcesAction()
      .then(setResources)
      .catch(() => {
        // A missing resource list only costs the filter dropdown; the calendar
        // itself still works, so this is not worth an error banner.
      });

    GetServicesAction()
      .then((data) => setServices(data || []))
      .catch(() => {
        // Same reasoning: the quick-booking form shows an empty service list
        // and the rest of the page is unaffected.
      });
  }, []);

  // The specialist a new booking most likely belongs to: the one the calendar
  // is filtered to, the only one there is (an employee sees just their own),
  // or the one the signed-in admin staffs. Otherwise the form asks.
  const defaultResourceId = useMemo(() => {
    if (resourceFilter) return resourceFilter;
    if (resources.length === 1) return String(resources[0].id);
    const own = userInfo?.userId && resources.find((r) => r.userId === userInfo.userId);
    return own ? String(own.id) : "";
  }, [resourceFilter, resources, userInfo]);

  // Only the first opener wins: a long press on a phone can report both a tap
  // and a selection, and one form is plenty.
  const openQuickBooking = (initial) =>
    setQuickBooking((current) => current ?? initial);

  const closeQuickBooking = useCallback(() => {
    setQuickBooking(null);
    calendarRef.current?.getApi().unselect();
  }, []);

  const fetchBookings = useCallback(async () => {
    if (!range) return;
    try {
      setLoading(true);
      const data = await GetCalendarBookingsAction(range.start, range.end, onlyMine);
      setBookings(data || []);
      setError(null);
    } catch (err) {
      console.error("Error loading calendar bookings:", err);
      setError(t("Dashboard.Calendar.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [range, onlyMine, t]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // `initialView` is only read when FullCalendar mounts, and the breakpoint is
  // not known until after the first paint. Drive the view from the breakpoint
  // instead, so a phone lands on its own view rather than a week grid squeezed
  // into 375px.
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.changeView(isMobile ? MOBILE_VIEW : DESKTOP_VIEW);
  }, [isMobile]);

  const events = useMemo(() => {
    return bookings
      .filter((b) => !resourceFilter || String(b.resourceId) === resourceFilter)
      .filter((b) => !statusFilter || b.status === statusFilter)
      .map((b) => {
        const colors = STATUS_COLORS[statusKey(b.status)] || STATUS_COLORS.pending;
        // The calendar's timezone is UTC, so the stamped value's clock fields
        // land in the matching grid cells regardless of the browser timezone.
        const start = new Date(b.bookingDateTime);
        const end = new Date(start.getTime() + (b.duration || 30) * 60000);
        return {
          id: String(b.id),
          title: b.customerName,
          start,
          end,
          backgroundColor: colors.bg,
          borderColor: colors.border,
          textColor: colors.text,
          // Cancelled bookings stay visible for context but must not be
          // rescheduled by dragging.
          editable: statusKey(b.status) !== "cancelled",
          extendedProps: { booking: b },
        };
      });
  }, [bookings, resourceFilter, statusFilter]);

  /**
   * Persist a drag. Reverts the event when the API refuses, so the calendar
   * never shows a position the server did not accept.
   */
  const handleEventDrop = async (info) => {
    const booking = info.event.extendedProps.booking;
    const start = info.event.start;

    try {
      await UpdateBookingAction(booking.id, {
        bookingDate: toApiDate(start),
        timeSlot: toApiTimeSlot(start),
      });
      notify.success(t("Dashboard.Bookings.notices.moved"));
      await fetchBookings();
    } catch (err) {
      info.revert();
      // Someone else holds that time: nothing is broken and the booking is
      // back where it was, so this is a warning rather than an error.
      if (err.response?.status === 409) {
        notify.warning(t("Dashboard.Calendar.errors.slotTaken"));
      } else {
        notifySaveError(err, t("Dashboard.Calendar.errors.moveFailed"));
      }
    }
  };

  // Status and delete run from the detail modal, so they report through the
  // snackbar: a page alert would appear behind the modal.
  const handleStatusChange = async (bookingId, status) => {
    try {
      setBusy(true);
      await UpdateBookingStatusAction(bookingId, status);
      notify.success(
        t(BookingStatusNoticeKeys[status] ?? "Dashboard.Bookings.notices.updated"),
      );
      await fetchBookings();
      setSelected(null);
    } catch (err) {
      notifySaveError(err, t("Dashboard.Calendar.errors.statusFailed"));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm(t("Dashboard.Calendar.confirm.delete"))) return;
    try {
      setBusy(true);
      await DeleteBookingAction(bookingId);
      notify.success(t("Dashboard.Bookings.notices.deleted"));
      await fetchBookings();
      setSelected(null);
    } catch (err) {
      notifySaveError(err, t("Dashboard.Calendar.errors.deleteFailed"));
    } finally {
      setBusy(false);
    }
  };

  const formatUtc = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString(i18n.language, {
      timeZone: "UTC",
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className={pageTitleClass}>{t("Dashboard.Calendar.title")}</h1>
          <p className={pageSubtitleClass}>
            {isAdmin
              ? t("Dashboard.Calendar.subtitleAdmin")
              : t("Dashboard.Calendar.subtitle")}
          </p>
        </div>
        <button
          type="button"
          className={`${btnPrimaryClass} shrink-0`}
          onClick={() => openQuickBooking({ date: today(), time: "", durationMinutes: null })}
        >
          <i className="bi bi-plus-lg" aria-hidden="true" />
          {t("Dashboard.Calendar.quick.open")}
        </button>
      </div>

      {error && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-circle mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button
            className="shrink-0 cursor-pointer rounded p-0.5 text-red-700 transition hover:bg-red-100"
            onClick={() => setError(null)}
            aria-label={t("Dashboard.Calendar.dismissError")}
          >
            <i className="bi bi-x text-lg" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className={`${cardClass} mb-5`}>
        <div className={cardBodyClass}>
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(200px,100%),1fr))]">
            <div>
              <label htmlFor="cal-resource" className={formLabelClass}>
                {t("Dashboard.Calendar.filters.resource")}
              </label>
              <select
                id="cal-resource"
                value={resourceFilter}
                onChange={(e) => setResourceFilter(e.target.value)}
                className={formControlClass}
              >
                <option value="">{t("Dashboard.Calendar.filters.allResources")}</option>
                {resources.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="cal-status" className={formLabelClass}>
                {t("Dashboard.Calendar.filters.status")}
              </label>
              <select
                id="cal-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={formControlClass}
              >
                <option value="">{t("Dashboard.Calendar.filters.allStatuses")}</option>
                <option value="Pending">{t("Dashboard.Bookings.status.pending")}</option>
                <option value="Confirmed">{t("Dashboard.Bookings.status.confirmed")}</option>
                <option value="Completed">{t("Dashboard.Bookings.status.completed")}</option>
                <option value="Cancelled">{t("Dashboard.Bookings.status.cancelled")}</option>
              </select>
            </div>
          </div>

          {/* "Only mine" is a view choice for admins; employees are already
              confined to their own bookings, so offering them a toggle would
              imply a choice they do not have. */}
          {isAdmin && (
            <div className="mt-4 border-t border-ink-200 pt-4">
              <label className="flex w-fit cursor-pointer items-center gap-2.5 text-fluid-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={onlyMine}
                  onChange={(e) => setOnlyMine(e.target.checked)}
                  className="size-4 cursor-pointer accent-brand-600"
                />
                {t("Dashboard.Calendar.filters.onlyMine")}
              </label>

              {onlyMine && !ownsAnyResource && (
                <p className="mt-2 text-xs text-ink-500">
                  {t("Dashboard.Calendar.filters.onlyMineNoResource")}
                </p>
              )}
            </div>
          )}

          {/* Legend — colour alone should not carry the status. */}
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 border-t border-ink-200 pt-4">
            {["pending", "confirmed", "completed", "cancelled"].map((s) => (
              <span key={s} className="flex items-center gap-1.5 text-xs text-ink-600">
                <span
                  className="size-3 rounded-sm border"
                  style={{
                    backgroundColor: STATUS_COLORS[s].bg,
                    borderColor: STATUS_COLORS[s].border,
                  }}
                />
                {t(`Dashboard.Bookings.status.${s}`)}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className={cardClass}>
        <div className={`${cardBodyClass} relative`}>
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
              <div
                role="status"
                aria-label={t("Dashboard.Calendar.loading")}
                className={spinnerClass}
              />
            </div>
          )}

          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView={isMobile ? MOBILE_VIEW : DESKTOP_VIEW}
            timeZone="UTC"
            locale={i18n.language}
            // A phone opens on the day's agenda: only the bookings, in order. The
            // hour grid it used to open on is sixteen hours of 80px rows, so a
            // day with three bookings was mostly empty cells to scroll past. The
            // grid stays one tap away ("By hour") for dragging a booking or
            // tapping a free time; "New booking" above does the latter anyway.
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: isMobile
                ? "listDay,listWeek,timeGridDay"
                : "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
            }}
            // Keyed by view on a phone, where both list views need their own
            // name: FullCalendar reads a `list` key for every list view before
            // it looks at the view's own, so that key must be absent there.
            buttonText={
              isMobile
                ? {
                    today: t("Dashboard.Calendar.views.today"),
                    listDay: t("Dashboard.Calendar.views.day"),
                    listWeek: t("Dashboard.Calendar.views.week"),
                    timeGridDay: t("Dashboard.Calendar.views.byHour"),
                  }
                : {
                    today: t("Dashboard.Calendar.views.today"),
                    month: t("Dashboard.Calendar.views.month"),
                    week: t("Dashboard.Calendar.views.week"),
                    day: t("Dashboard.Calendar.views.day"),
                    list: t("Dashboard.Calendar.views.list"),
                  }
            }
            views={{
              list: { eventContent: renderListEvent },
              listDay: { noEventsContent: t("Dashboard.Calendar.noBookingsDay") },
              listWeek: { noEventsContent: t("Dashboard.Calendar.noBookingsWeek") },
            }}
            events={events}
            editable
            eventStartEditable
            // Duration is derived from the booking's service, and the API
            // recomputes it on every save. Letting someone resize an event
            // would look like it worked and then silently snap back.
            eventDurationEditable={false}
            // Click a free cell, or drag across a stretch of them, to book it.
            // The selection only prefills the form: the time is then matched
            // against the slots the API offers, which is the one definition of
            // bookable. Overlapping a booking is allowed on purpose — in the
            // all-specialists view the busy one may not be the one booked.
            selectable
            selectMirror
            selectAllow={(info) => info.end.getTime() > nowOnGrid()}
            select={(info) => openQuickBooking(selectionToQuickBooking(info))}
            // A tap on a phone is a dateClick, not a selection (that takes a
            // long press). Mouse clicks are already handled by `select` above.
            dateClick={(info) => {
              if (!String(info.jsEvent?.type || "").startsWith("touch")) return;
              const end = new Date(info.date.getTime() + SNAP_MINUTES * 60000);
              if (end.getTime() <= nowOnGrid()) return;
              openQuickBooking(
                selectionToQuickBooking({ start: info.date, end, allDay: info.allDay }),
              );
            }}
            dayMaxEvents={3}
            nowIndicator
            // Touch: a long press starts a drag, so a scroll gesture on a phone
            // does not fling bookings around the grid.
            longPressDelay={300}
            eventLongPressDelay={300}
            selectLongPressDelay={300}
            height="auto"
            slotDuration={`00:${SLOT_MINUTES}:00`}
            snapDuration={`00:${SNAP_MINUTES}:00`}
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            allDaySlot={false}
            datesSet={(info) => {
              const start = info.start.toISOString();
              const end = info.end.toISOString();
              setRange((prev) =>
                prev && prev.start === start && prev.end === end ? prev : { start, end },
              );
            }}
            eventDrop={handleEventDrop}
            eventClick={(info) => setSelected(info.event.extendedProps.booking)}
          />
        </div>
      </div>

      {selected && (
        <div
          className={modalOverlayClass}
          onClick={() => setSelected(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="calendar-detail-title"
        >
          <div className={modalPanelClass} onClick={(e) => e.stopPropagation()}>
            <div className={modalHeaderClass}>
              <h2 id="calendar-detail-title" className={cardTitleClass}>
                {t("Dashboard.Calendar.detail.title")}
              </h2>
              <button
                className={modalCloseClass}
                onClick={() => setSelected(null)}
                aria-label={t("Dashboard.Bookings.modal.close")}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className={modalBodyClass}>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <span
                  className={`${badgeClass}`}
                  style={{
                    backgroundColor:
                      (STATUS_COLORS[statusKey(selected.status)] || STATUS_COLORS.pending).bg,
                    color:
                      (STATUS_COLORS[statusKey(selected.status)] || STATUS_COLORS.pending).text,
                  }}
                >
                  {selected.status}
                </span>
                <span className="rounded-md bg-ink-100 px-2 py-1 font-mono text-xs font-semibold text-ink-700">
                  {selected.confirmationCode}
                </span>
              </div>

              <dl className="grid gap-3 sm:grid-cols-2">
                {[
                  ["customer", selected.customerName],
                  ["email", selected.customerEmail || "-"],
                  ["phone", selected.customerPhone || "-"],
                  ["service", selected.serviceName],
                  ["resource", selected.resourceName],
                  ["when", formatUtc(selected.bookingDateTime)],
                ].map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-xs font-semibold text-ink-500">
                      {t(`Dashboard.Calendar.detail.${key}`)}
                    </dt>
                    <dd className="m-0 text-fluid-sm break-words text-ink-900">{value}</dd>
                  </div>
                ))}
              </dl>

              {selected.notes && (
                <div className="mt-4">
                  <dt className="text-xs font-semibold text-ink-500">
                    {t("Dashboard.Calendar.detail.notes")}
                  </dt>
                  <dd className="m-0 text-fluid-sm text-ink-900">{selected.notes}</dd>
                </div>
              )}
            </div>

            <div className={`${modalFooterClass} flex-wrap`}>
              {selected.status === "Pending" && (
                <button
                  className={btnPrimaryClass}
                  disabled={busy}
                  onClick={() => handleStatusChange(selected.id, "Confirmed")}
                >
                  <i className="bi bi-check-lg" />
                  {t("Dashboard.Calendar.actions.approve")}
                </button>
              )}
              {(selected.status === "Pending" || selected.status === "Confirmed") && (
                <button
                  className={btnSecondaryClass}
                  disabled={busy}
                  onClick={() => handleStatusChange(selected.id, "Cancelled")}
                >
                  <i className="bi bi-x-lg" />
                  {t("Dashboard.Calendar.actions.reject")}
                </button>
              )}
              {selected.status === "Confirmed" && (
                <button
                  className={btnSecondaryClass}
                  disabled={busy}
                  onClick={() => handleStatusChange(selected.id, "Completed")}
                >
                  <i className="bi bi-check-circle" />
                  {t("Dashboard.Calendar.actions.complete")}
                </button>
              )}
              <button
                className={btnDangerClass}
                disabled={busy}
                onClick={() => handleDelete(selected.id)}
              >
                <i className="bi bi-trash" />
                {t("Dashboard.Calendar.actions.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {quickBooking && (
        <QuickBookingModal
          initial={quickBooking}
          resources={resources}
          services={services}
          defaultResourceId={defaultResourceId}
          onClose={closeQuickBooking}
          onCreated={() => {
            closeQuickBooking();
            fetchBookings();
          }}
        />
      )}
    </div>
  );
};

export default Calendar;
